import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

// Minimum deliberate horizontal drag, in px, to count as a swipe rather than
// an incidental tap or a vertical page scroll.
const SWIPE_THRESHOLD_PX = 40;
// Any movement below this, in px (either axis), still counts as a stationary
// tap rather than a drag — a real tap on a CTA or the slide's own link still
// activates normally (see `#handleClick`).
const TAP_MOVE_TOLERANCE_PX = 10;
// Once total movement passes this, in px, the gesture's direction is locked
// in from whichever axis dominates so far — see `#handlePointerMove`.
const DIRECTION_LOCK_MOVE_PX = TAP_MOVE_TOLERANCE_PX;

/**
 * Full-viewport hero slider.
 *
 * - Soft cross-fade between slide media (handled in CSS, >= 500ms).
 * - Staggered text entrance / exit per slide (handled in CSS).
 * - Each slide's progress bar fills left -> right over the slide duration; when
 *   the active bar reaches the end the slider advances to the next slide.
 * - Pauses when the tab is hidden; respects prefers-reduced-motion.
 *
 * Timing is owned by the CSS `hero-progress` animation (duration
 * `--hero-interval`): the slider advances on its `animationend`, so the bar and
 * the slide change stay perfectly in sync. This element only tracks which slide
 * is current and pauses/resumes the animation.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [slides]
 *
 * @extends {Component<Refs>}
 */
export class HeroSlider extends Component {
  #current = 0;
  #pointerDownX = 0;
  #pointerDownY = 0;
  // Whether the gesture that just ended moved enough to count as a drag
  // rather than a stationary tap — checked by `#handleClick` so a swipe that
  // happened to end over the slide's full-cover link doesn't also navigate
  // through it. A real tap (no meaningful movement) still navigates.
  #wasDrag = false;
  // `null` until `DIRECTION_LOCK_MOVE_PX` of total movement has happened in
  // the current gesture, then locked to whichever axis dominates at that
  // point. Read by `#handleTouchMove` to decide whether *this* gesture should
  // cancel the hero's native `touch-action: pan-y` scroll — see that
  // handler's own comment for why suppression has to happen from a real
  // `touchmove` listener rather than from `#handlePointerMove` itself.
  /** @type {'horizontal' | 'vertical' | null} */
  #dragDirection = null;

  connectedCallback() {
    super.connectedCallback();

    // Videos ship without `autoplay` and with `preload='none'` (see
    // `snippets/hero-video.liquid`): a CSS-hidden autoplaying video still
    // downloads in full, so both breakpoint copies of every slide's video were
    // fetched upfront. Playback starts here instead, only for the copies
    // visible at the current breakpoint, and re-syncs when the viewport
    // crosses the seam.
    //
    // Both listeners sit above the single-slide early return: without the
    // `autoplay` attribute the browser never retries playback on its own (a
    // page loaded in a background tab stays paused - Safari only auto-resumes
    // videos that carry the attribute), so visibility handling is what brings
    // a backgrounded hero back to life, single-slide ones included.
    this.#breakpoint.addEventListener('change', this.#handleBreakpointChange);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    if (this.count <= 1) {
      if (prefersReducedMotion()) this.#pauseVideos();
      this.#syncVideos(0);
      return;
    }

    if (prefersReducedMotion()) this.#pauseVideos();

    this.addEventListener('animationend', this.#handleAnimationEnd);
    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('pointermove', this.#handlePointerMove);
    this.addEventListener('pointerup', this.#handlePointerUp);
    this.addEventListener('pointercancel', this.#handlePointerCancel);
    // `passive: false` so `preventDefault()` inside can actually cancel the
    // browser's native scroll. A real `touchmove` listener, not `pointermove`
    // — see `#handleTouchMove`'s own comment for why.
    this.addEventListener('touchmove', this.#handleTouchMove, { passive: false });
    // Each slide's full-cover link (and any media inside it) is natively
    // draggable, so a swipe starting on one would otherwise trigger the
    // browser's own HTML5 drag-and-drop instead of a pointer gesture, which
    // cancels this element's pointerup entirely (fires `pointercancel`
    // instead). Suppressing native drag here is the only way to keep
    // swiping over the slide working.
    this.addEventListener('dragstart', this.#handleDragStart);
    // Suppressing `dragstart` above means a swipe that ends over the slide's
    // link falls through to a normal click — capture phase so this runs
    // before the link's own navigation.
    this.addEventListener('click', this.#handleClick, true);

    this.current = this.#current;
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#breakpoint.removeEventListener('change', this.#handleBreakpointChange);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('pointermove', this.#handlePointerMove);
    this.removeEventListener('pointerup', this.#handlePointerUp);
    this.removeEventListener('pointercancel', this.#handlePointerCancel);
    this.removeEventListener('touchmove', this.#handleTouchMove);
    this.removeEventListener('dragstart', this.#handleDragStart);
    this.removeEventListener('click', this.#handleClick, true);
  }

  get count() {
    return this.refs.slides?.length ?? 0;
  }

  /**
   * Scroll the viewer to the first visible section after this one. Bound from
   * the hero's scroll indicator via `on:click`.
   *
   * The sections are collected in document order rather than walked as DOM
   * siblings because the hero's `.shopify-section` wrapper is the last child of
   * `<main>` on some templates — from there the next section lives in the
   * footer group, outside the hero's parent.
   */
  scrollToNextSection() {
    const current = this.closest('.shopify-section');
    if (!current) return;

    const sections = Array.from(document.querySelectorAll('.shopify-section'));
    const index = sections.indexOf(current);
    if (index === -1) return;

    // Skip sections that render nothing (hidden or empty) so the scroll always
    // lands on something the viewer can actually see.
    const next = sections
      .slice(index + 1)
      .find((section) => section instanceof HTMLElement && section.offsetHeight > 0);
    if (!next) return;

    // Land the section's top below the sticky header rather than under it.
    // scrollIntoView can't take an offset, hence the manual arithmetic — and
    // the scroll container differs by breakpoint: `.page-wrapper` scrolls from
    // 990px up (base.css hides the document's overflow there), the document
    // below it.
    const target = next.getBoundingClientRect().top - this.#headerOffset;
    const scroller = this.#scroller;
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';

    if (scroller) {
      scroller.scrollTo({ top: scroller.scrollTop + target - scroller.getBoundingClientRect().top, behavior });
    } else {
      window.scrollTo({ top: window.scrollY + target, behavior });
    }
  }

  /** The scrolling ancestor, or null when the document itself scrolls. */
  get #scroller() {
    const wrapper = this.closest('.page-wrapper');
    if (!(wrapper instanceof HTMLElement)) return null;

    return getComputedStyle(wrapper).overflowY === 'auto' ? wrapper : null;
  }

  /**
   * How much of the viewport top the header covers once scrolled. header.js
   * keeps `--header-height` on <body> in sync with the header's rendered
   * height, and it's the same value the theme uses everywhere else to push
   * content out from under the header (see `--section-top-offset` in base.css).
   *
   * Only an `always` sticky header counts: `scroll-up` hides the header while
   * scrolling down, which is the only direction this scroll ever goes, and a
   * non-sticky header has already scrolled away with the hero.
   */
  get #headerOffset() {
    if (!document.querySelector('header-component[sticky="always"]')) return 0;

    const height = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height'));

    return Number.isFinite(height) ? height : 0;
  }

  pause = () => {
    this.setAttribute('paused', '');
  };

  resume = () => {
    this.removeAttribute('paused');
    // Scripted play() doesn't get the browser's autoplay-attribute
    // auto-resume, so coming back to the tab has to restart the visible video
    // explicitly - removing `paused` alone only unfreezes the progress bar.
    this.#syncVideos(this.#current);
  };

  next() {
    this.select(this.#current + 1);
  }

  previous() {
    this.select(this.#current - 1);
  }

  /**
   * Navigate to a slide by index (wraps around). Restarting the active slide's
   * progress bar gives every selection a full dwell before the next advance.
   * @param {number} index
   */
  select(index) {
    const { count } = this;
    if (count === 0) return;

    let next = index % count;
    if (next < 0) next += count;

    this.current = next;
  }

  get current() {
    return this.#current;
  }

  set current(index) {
    this.#current = index;

    const slides = this.refs.slides ?? [];

    slides.forEach((slide, i) => {
      slide.toggleAttribute('active', i === index);
    });

    // Only the active slide's video decodes; inactive slides' videos are paused
    // to avoid decoding 2–3 hidden videos for the whole page lifetime.
    this.#syncVideos(index);

    // Each slide carries its own indicator row with static done/current classes,
    // so activating the slide is all that's needed. Restart the current slide's
    // progress bar so every selection gets a full dwell before the next advance.
    const active = slides[index];
    const fill = active?.querySelector('.hero__indicator--current .hero__indicator-fill');
    if (fill instanceof HTMLElement) {
      fill.style.animation = 'none';
      void fill.offsetWidth;
      fill.style.animation = '';
    }
  }

  /**
   * Advance once the active slide's progress bar has filled completely.
   * @param {AnimationEvent} event
   */
  #handleAnimationEnd = (event) => {
    if (event.animationName !== 'hero-progress') return;
    if (prefersReducedMotion()) return;
    this.next();
  };

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    if (!event.isPrimary) return;

    this.#pointerDownX = event.clientX;
    this.#pointerDownY = event.clientY;
    // A fresh gesture always starts a clean slate — without this, a click
    // that follows a swipe with no new pointer gesture in between (keyboard
    // Enter/Space, assistive-tech activation) would still be judged by the
    // *previous* swipe's value and get killed by `#handleClick` below.
    this.#wasDrag = false;
    this.#dragDirection = null;
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    if (!event.isPrimary) return;
    if (this.#dragDirection !== null) return; // already locked for this gesture

    const dx = event.clientX - this.#pointerDownX;
    const dy = event.clientY - this.#pointerDownY;
    if (Math.max(Math.abs(dx), Math.abs(dy)) < DIRECTION_LOCK_MOVE_PX) return;
    this.#dragDirection = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
  };

  /**
   * Cancels the hero's native `touch-action: pan-y` scroll for gestures
   * `#handlePointerMove` locked "horizontal" — a real `TouchEvent`, not the
   * `PointerEvent` driving the rest of this gesture tracking, because iOS
   * Safari doesn't reliably honor `preventDefault()` called from
   * `pointermove` to cancel an in-progress (or about-to-start) native pan; it
   * does honor it here. Runs after `#handlePointerMove` for the same
   * physical move (browsers dispatch the pointer event first), so
   * `#dragDirection` already reflects this event's own movement.
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    if (this.#dragDirection === 'horizontal') event.preventDefault();
  };

  /** @param {PointerEvent} event */
  #handlePointerUp = (event) => {
    if (!event.isPrimary) return;

    const dx = event.clientX - this.#pointerDownX;
    const dy = event.clientY - this.#pointerDownY;

    this.#wasDrag = Math.abs(dx) > TAP_MOVE_TOLERANCE_PX || Math.abs(dy) > TAP_MOVE_TOLERANCE_PX;

    // Only a clear, mostly-horizontal drag past the threshold counts — a
    // drag that's more vertical than horizontal is left alone so the user
    // can still scroll the page.
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > SWIPE_THRESHOLD_PX) {
      if (dx < 0) this.next();
      else this.previous();
    }

    this.#dragDirection = null;
  };

  /** @param {PointerEvent} event */
  #handlePointerCancel = (event) => {
    if (!event.isPrimary) return;
    this.#dragDirection = null;
  };

  /** @param {DragEvent} event */
  #handleDragStart = (event) => {
    event.preventDefault();
  };

  /**
   * A swipe that ends over the active slide's full-cover link would
   * otherwise still navigate to that link's destination once native drag is
   * suppressed above.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!this.#wasDrag) return;
    event.preventDefault();
    event.stopPropagation();
  };

  #pauseVideos() {
    for (const video of this.querySelectorAll('video')) {
      video.removeAttribute('autoplay');
      video.pause();
    }
  }

  #breakpoint = window.matchMedia('(min-width: 768px)');

  #handleBreakpointChange = () => this.#syncVideos(this.#current);

  /**
   * Play the active slide's visible video and pause every other one, so only
   * the media the visitor can see downloads and decodes: each slide carries up
   * to two breakpoint copies of its video and CSS hides the wrong one, but
   * play() would fetch a hidden copy all the same. Skips playback under
   * prefers-reduced-motion (those videos never start).
   * @param {number} activeIndex
   */
  #syncVideos(activeIndex) {
    if (prefersReducedMotion()) return;

    const slides = this.refs.slides?.length ? this.refs.slides : [this];
    slides.forEach((slide, i) => {
      for (const video of slide.querySelectorAll('video')) {
        if (i === activeIndex && this.#isRendered(video)) {
          // The video is transparent over its poster image until frames are
          // actually rendering (see snippets/hero-video.liquid) - stamped on
          // 'playing' rather than on play(), which resolves before paint.
          if (!video.dataset.playing) {
            video.addEventListener('playing', () => (video.dataset.playing = 'true'), { once: true });
          }
          const playback = video.play();
          if (playback) playback.catch(() => {});
        } else {
          video.pause();
        }
      }
    });
  }

  /**
   * Whether the element is actually rendered (not display:none from the
   * breakpoint classes). checkVisibility with a getClientRects fallback for
   * engines that don't ship it yet.
   * @param {HTMLElement} element
   */
  #isRendered(element) {
    if (typeof element.checkVisibility === 'function') return element.checkVisibility();
    return element.getClientRects().length > 0;
  }

  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('hero-slider-component')) {
  customElements.define('hero-slider-component', HeroSlider);
}
